const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'https://ourzestapp.online/oz_backend').replace(/\/$/, '');
const TOKEN_KEY = 'ourzest_secure_session_token';
const DEVICE_KEY = 'ourzest_device_id';
const SESSION_ENDED_KEY = 'ourzest_secure_session_ended';
const CLIENT_BUILD = 'localhost-safe-v3';
let upgradePromise = null;

if (process.env.NODE_ENV === 'development') {
  console.info(`[RootScurityClient] ${CLIENT_BUILD}`);
}

function isCrossOriginApi() {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(API_BASE, window.location.href).origin !== window.location.origin;
  } catch {
    return true;
  }
}

function attachSecureHeaders(headers, token) {
  const crossOrigin = isCrossOriginApi();

  // Remove incompatible caller-supplied headers as well. This guarantees that
  // localhost preflights never contain these names, even when an older caller
  // passes them through options.headers.
  if (crossOrigin) {
    headers.delete('X-Session-Token');
    headers.delete('X-Device-Id');
  }

  if (!token) return;

  // Authorization is explicitly allowed by the legacy Hostinger CORS layer.
  headers.set('Authorization', `Bearer ${token}`);

  // Preserve the shared-hosting fallback headers for the deployed same-origin
  // application. On localhost, omitting them keeps the preflight compatible
  // with older server-level CORS configurations that Hostinger may still serve.
  if (!crossOrigin) {
    headers.set('X-Session-Token', token);
    headers.set('X-Device-Id', getDeviceId());
  }
}

function storedToken() {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
}

function randomId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function getDeviceId() {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = randomId();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function secureLoginPayload(credentials, applicationCode, applicationId = null) {
  // An explicit credential submission is the only operation allowed to start a
  // new session after an authoritative forced logout.
  sessionStorage.removeItem(SESSION_ENDED_KEY);
  return {
    ...credentials,
    application_code: applicationCode,
    application_id: applicationId,
    device_id: getDeviceId(),
    device_name: `${navigator.platform || 'Web'} · ${navigator.userAgentData?.brands?.[0]?.brand || 'Browser'}`,
  };
}

export function acceptSecureLogin(response) {
  const token = response?.secure_session?.session_token || response?.session_token;
  if (!token) throw new Error('The server did not issue a secure session token. Apply the login integration patch.');
  sessionStorage.setItem(TOKEN_KEY, token);
  // AuthContext persists the user in localStorage, so the corresponding bearer
  // token must survive a reload/new tab as well. It remains server-revocable.
  localStorage.setItem(TOKEN_KEY, token);
  sessionStorage.removeItem(SESSION_ENDED_KEY);
  return token;
}

function storedLegacySessionId() {
  const direct = localStorage.getItem('session_id');
  if (direct) return direct;
  try {
    return JSON.parse(localStorage.getItem('user') || '{}').session_id || null;
  } catch { return null; }
}

export async function upgradeLegacySession(legacySessionId = storedLegacySessionId(), applicationCode = 'root-portal') {
  const current = storedToken();
  if (current) return current;
  if (sessionStorage.getItem(SESSION_ENDED_KEY)) {
    throw Object.assign(new Error('This session was ended by the server. Sign in again.'), { code: 'SESSION_ENDED' });
  }
  if (!legacySessionId) throw new Error('The login response did not provide a session_id. Add session_id to Login.js userData.');
  if (upgradePromise) return upgradePromise;

  upgradePromise = (async () => {
    const response = await fetch(`${API_BASE}/root_session_upgrade.php?_oz=${Date.now()}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        legacy_session_id: legacySessionId,
        device_id: getDeviceId(),
        device_name: `${navigator.platform || 'Web'} · ${navigator.userAgentData?.brands?.[0]?.brand || 'Browser'}`,
        application_code: applicationCode,
        remember: localStorage.getItem('rememberMe') === 'true',
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) throw Object.assign(new Error(data.error || 'Could not verify login session'), { status: response.status, data });
    return acceptSecureLogin(data);
  })();

  try { return await upgradePromise; }
  finally { upgradePromise = null; }
}

export function clearSecureSession(terminated = false) {
  sessionStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY);
  if (terminated) sessionStorage.setItem(SESSION_ENDED_KEY, String(Date.now()));
}

export function hasSecureSession() {
  return Boolean(storedToken());
}

function withCacheBuster(path) {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}_oz=${Date.now()}`;
}

export async function rootApi(path, options = {}) {
  let token = storedToken();
  if (!token && !sessionStorage.getItem(SESSION_ENDED_KEY) && !path.includes('root_session_upgrade.php')) {
    try { token = await upgradeLegacySession(); } catch { /* API returns the authoritative 401 below. */ }
  }
  const headers = new Headers(options.headers || {});
  attachSecureHeaders(headers, token);
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const response = await fetch(`${API_BASE}/${withCacheBuster(path.replace(/^\//, ''))}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  let data;
  try { data = await response.json(); } catch { data = { error: `HTTP ${response.status}` }; }
  const sessionEndCodes = [
    'SESSION_REPLACED', 'SESSION_REVOKED', 'SESSION_EXPIRED',
    'AUTH_REQUIRED', 'IP_BLOCKED', 'IP_NOT_APPROVED', 'USER_INACTIVE',
    'APPLICATION_ACCESS_REVOKED',
  ];
  if ([401, 403].includes(response.status) && sessionEndCodes.includes(data.code)) {
    clearSecureSession(true);
    window.dispatchEvent(new CustomEvent('ourzest:session-ended', { detail: data }));
  }
  if (!response.ok || data.success === false) throw Object.assign(new Error(data.error || data.message || 'Request failed'), { status: response.status, data });
  return data;
}

/**
 * Drop-in replacement for fetch() used by the preserved legacy management
 * screens. It keeps their existing response parsing and full feature set while
 * attaching the same revocable bearer session used by the Control Center.
 */
export async function rootFetch(input, options = {}) {
  let token = storedToken();
  if (!token && !sessionStorage.getItem(SESSION_ENDED_KEY)) {
    try { token = await upgradeLegacySession(); } catch { /* Preserve the endpoint's own response. */ }
  }
  const headers = new Headers(options.headers || {});
  attachSecureHeaders(headers, token);
  const cacheBustedInput = typeof input === 'string' ? withCacheBuster(input) : input;
  const response = await fetch(cacheBustedInput, {
    ...options,
    headers,
    credentials: 'include',
  });
  if ([401, 403].includes(response.status)) {
    const data = await response.clone().json().catch(() => ({}));
    const sessionEndCodes = [
      'SESSION_REPLACED', 'SESSION_REVOKED', 'SESSION_EXPIRED',
      'AUTH_REQUIRED', 'IP_BLOCKED', 'IP_NOT_APPROVED', 'USER_INACTIVE',
      'APPLICATION_ACCESS_REVOKED',
    ];
    if (sessionEndCodes.includes(data.code)) {
      clearSecureSession(true);
      window.dispatchEvent(new CustomEvent('ourzest:session-ended', { detail: data }));
    }
  }
  return response;
}

export async function rootAction(path, action, payload = {}) {
  return rootApi(path, { method: 'POST', body: JSON.stringify({ action, ...payload }) });
}

export { API_BASE };
